import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { TIME_INTERVALS } from "shared/lib/constants/time";
import { usePollableMutation } from "shared/lib/hooks/use-react-query-extensions";

import { useApiClient } from "@/context/api-client";
import { UpdateCampaignCustomScriptRequestParamsI } from "@/api/glencoco";
import { AccountDetailsQueryKeys } from "@/modules/pipeline/account-details/data/keys";
import {
  GetShouldSendEmailRequestParamsI,
  GetShouldSendEmailResponseI,
} from "@/api/routes/email";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";

import { swallow } from "@/api/legacy-swallow";

interface UseUpdateCampaignScriptParamsI {
  campaignId: string;
  params: UpdateCampaignCustomScriptRequestParamsI;
}

export const useUpdateCampaignScript = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, params }: UseUpdateCampaignScriptParamsI) =>
      api.updateCampaignCustomScript(campaignId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AccountDetailsQueryKeys.script,
      });
    },
  });
};

export const useResetCampaignScript = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) =>
      api.resetCampaignCustomScript(campaignId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: AccountDetailsQueryKeys.script,
      });
    },
  });
};

interface GetShouldSendEmailFnParamsI {
  campaignId: string;
  callId: string;
  parameters: GetShouldSendEmailRequestParamsI;
}

export const getShouldSendFollowUpEmailFn = (
  api: ReturnType<typeof useApiClient>,
  setEmailFollowUpRequestParams?: (
    params: GetShouldSendEmailRequestParamsI
  ) => void
) => {
  return async ({
    campaignId,
    callId,
    parameters,
  }: GetShouldSendEmailFnParamsI) => {
    /** We need to set the parameters to the context
     * So we could try polling last selected parameters
     * after disposition submission or booked meeting
     */
    setEmailFollowUpRequestParams?.(parameters);

    return swallow(
      api.email.getShouldSendFollowUpEmail(campaignId, callId, parameters)
    );
  };
};

export const useShouldSendFollowUpEmail = () => {
  const api = useApiClient();
  const { setEmailFollowUpRequestParams } = useAccountDetailsContext();

  const mutationFn = getShouldSendFollowUpEmailFn(
    api,
    setEmailFollowUpRequestParams
  );

  return useMutation({
    mutationFn: mutationFn,
  });
};

export const getShouldSendFollowUpEmailAxiosFn =
  (api: ReturnType<typeof useApiClient>) =>
  async ({ campaignId, callId, parameters }: GetShouldSendEmailFnParamsI) => {
    const response: AxiosResponse<GetShouldSendEmailResponseI> = await swallow(
      api.email.getShouldSendFollowUpEmail(campaignId, callId, parameters)
    );
    return response.data; // Extract the data from AxiosResponse
  };

export const useShouldSendFollowUpEmailPolling = () => {
  const api = useApiClient();
  const mutationFn = getShouldSendFollowUpEmailAxiosFn(api);

  return usePollableMutation<
    GetShouldSendEmailResponseI,
    GetShouldSendEmailFnParamsI
  >(mutationFn, {
    queryKey: AccountDetailsQueryKeys.shouldSendEmailPolling,
    pollingInterval: TIME_INTERVALS.ONE_SECOND,
    maxAttempts: 20,
    shouldContinuePolling: (data) => !data.email_subject || !data.email_body,
  });
};

interface MoveMeetingParamsI {
  campaignId: string;
  accountId: string;
  contactId: string;
  // ISO 8601 instant, already combined from the rep's date/time/zone pick.
  meetingTime: string;
  // GLE-8478: the IANA zone of that pick. The card shows the meeting in it.
  meetingTimezone?: string;
}

/**
 * GLE-8249: moves the detected calendar event in place. The card reads the
 * event through the BMCE query, so a success drops that cache; otherwise the
 * rep keeps seeing the old time until the 30 s staleTime runs out.
 */
export const useMutationMoveMeeting = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      accountId,
      contactId,
      meetingTime,
      meetingTimezone,
    }: MoveMeetingParamsI) =>
      apiClient.scheduling.moveMeeting(campaignId, accountId, contactId, {
        meeting_time: meetingTime,
        meeting_timezone: meetingTimezone,
      }),
    onSuccess: (_response, { campaignId, accountId }) => {
      void queryClient.invalidateQueries({
        queryKey: AccountDetailsQueryKeys.bookedMeetingCalendarEvent(
          campaignId,
          accountId
        ),
      });
    },
  });
};

export const useMutationRequestSyncCalendarEventForAccount = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      campaignId,
    }: {
      accountId: string | undefined;
      campaignId: string | undefined;
    }) => {
      if (!accountId || !campaignId) {
        throw new Error("accountId and campaignId are required");
      }

      const response =
        await apiClient.bookedMeetingCalendarEvents.getForAccount(
          campaignId,
          accountId,
          true // Always sync against calendar when mutating
        );

      if (response.status !== 200) {
        throw new Error(
          `Failed to fetch booked meeting calendar event for account: ${accountId} (campaign: ${campaignId})`
        );
      }

      return response.data.event;
    },
    onSuccess: (response, { accountId, campaignId }) => {
      if (response && accountId && campaignId) {
        queryClient.setQueryData(
          AccountDetailsQueryKeys.bookedMeetingCalendarEvent(
            campaignId,
            accountId
          ),
          response
        );
      }
    },
  });
};
